import { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, articleCategories } from '@/mocks/articles';
import type { Article } from '@/mocks/articles';

export default function ArticlesListPage() {
  const { slug } = useParams<{ slug?: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const activeCategory = slug || 'all';

  const filteredArticles: Article[] = activeCategory === 'all'
    ? articles
    : articles.filter((a) => a.categorySlug === activeCategory);

  return (
    <>
      <Navbar />
      <main>
        <section className="pt-28 pb-10 md:pt-32 md:pb-14 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900">文章资讯</span>
            </nav>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
              文章资讯
            </h1>
            <p className="mt-2 text-foreground-500 text-sm">
              了解 aiye 最新动态、技术分享与行业洞察
            </p>
          </div>
        </section>

        <section className="py-0 pb-6 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-wrap gap-2 md:gap-3">
              {articleCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'all' ? '/articles' : `/articles/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                  <span className={`ml-1.5 text-xs ${activeCategory === cat.slug ? 'text-white/70' : 'text-foreground-400'}`}>
                    {cat.count}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            {filteredArticles.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-article-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group rounded-2xl bg-background-100 border border-background-200/70 overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5 md:p-6">
                      <div className="flex items-center gap-3 text-xs text-foreground-400 mb-3">
                        <span className="px-2.5 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">
                          {article.category}
                        </span>
                        <span>{article.date}</span>
                        <span>{article.readTime}</span>
                      </div>
                      <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-4">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}